import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import { useLang } from "@/hooks/useLang";
import { BUSINESS, SERVICES, serviceUrl, serviceTitle } from "@/content/site";

// Module-level constant so this list doesn't create a new array every render.
const COLOR_TOKENS = [
  { name: "Primary (Maroon)", var: "--gw-primary", val: "#7A1B2A" },
  { name: "Primary Dark", var: "--gw-primary-dark", val: "#3B0A14" },
  { name: "Accent (Gold)", var: "--gw-accent", val: "#D4A017" },
  { name: "Surface", var: "--gw-surface", val: "#FAF8F5" },
  { name: "Text", var: "--gw-text", val: "#1F1F1F" },
  { name: "Border", var: "--gw-border", val: "#E7E2DA" },
];

export default function StyleguidePage({ lang: propLang }) {
  const detected = useLang();
  const lang = propLang || detected;
  const path = lang === "es" ? "/es/styleguide" : "/styleguide";
  const pair = lang === "es" ? "/styleguide" : "/es/styleguide";

  const page = useMemo(() => ({
    title: lang === "en" ? "Design Styleguide | Garcia-Windsor, P.C." : "Guía de Estilo | Garcia-Windsor, P.C.",
    description: lang === "en" ? "Component library and design tokens for Garcia-Windsor, P.C." : "Biblioteca de componentes y tokens de diseño para Garcia-Windsor, P.C.",
  }), [lang]);

  const breadcrumbItems = useMemo(() => ([
    { label: lang === "en" ? "Home" : "Inicio", href: lang === "en" ? "/" : "/es" },
    { label: "Styleguide", href: path },
  ]), [lang, path]);

  return (
    <>
      <Seo page={page} path={path} lang={lang} pair={pair} noindex />
      <section className="gw-container gw-section">
        <Breadcrumbs items={breadcrumbItems} />
        <h1>{lang === "en" ? "Design Styleguide" : "Guía de Estilo"}</h1>
        <p className="gw-muted">{lang === "en" ? "Component library for the Garcia-Windsor, P.C. bilingual website." : "Biblioteca de componentes para el sitio bilingüe de Garcia-Windsor, P.C."}</p>

        <h2 style={{marginTop: 48}}>Colors</h2>
        <div className="gw-grid gw-grid-3">
          {COLOR_TOKENS.map((c) => (
            <div key={c.name} className="gw-card" style={{padding: 0, overflow: "hidden"}}>
              <div style={{background: c.val, height: 90}} />
              <div style={{padding: 14}}>
                <strong>{c.name}</strong><br />
                <code>{c.val}</code>
              </div>
            </div>
          ))}
        </div>

        <h2 style={{marginTop: 48}}>Typography</h2>
        <div className="gw-card">
          <h1>Heading 1 — Fraunces Serif</h1>
          <h2>Heading 2 — Fraunces Serif</h2>
          <h3>Heading 3 — Fraunces Serif</h3>
          <p>Body copy sample — Inter Sans. The quick brown fox jumps over the lazy dog. Spanish accents: á é í ó ú ñ ü ¿ ¡.</p>
        </div>

        <h2 style={{marginTop: 48}}>Buttons</h2>
        <div style={{display: "flex", gap: 12, flexWrap: "wrap"}}>
          <button className="gw-btn gw-btn-primary">Primary</button>
          <button className="gw-btn gw-btn-accent">Accent</button>
          <button className="gw-btn gw-btn-ghost">Ghost</button>
          <button className="gw-btn gw-btn-primary gw-btn-lg">Large Primary</button>
        </div>

        <h2 style={{marginTop: 48}}>Quotable Answer</h2>
        <div className="gw-quotable">
          <p>{lang === "en"
            ? "Garcia-Windsor, P.C. is a bilingual family law firm in Dallas serving Dallas County and DFW. Attorney Isabela Garcia is the exclusive consulting attorney for the Mexican Consulate General of Dallas."
            : "Garcia-Windsor, P.C. es un bufete bilingüe de derecho familiar en Dallas que sirve al condado de Dallas y DFW. La abogada Isabela García es la abogada consultora exclusiva del Consulado General de México en Dallas."}</p>
        </div>

        <h2 style={{marginTop: 48}}>Service Cards</h2>
        <div className="gw-grid gw-grid-3" style={{marginTop: 16}}>
          {SERVICES.slice(0, 3).map((s) => (
            <Link key={s.key} to={serviceUrl(s, lang)} className="gw-card gw-card-link">
              <h3>{serviceTitle(s, lang)}</h3>
              <p className="gw-muted">Sample service card copy for the styleguide preview.</p>
            </Link>
          ))}
        </div>

        <h2 style={{marginTop: 48}}>FAQ Accordion</h2>
        <div className="gw-faq">
          <details>
            <summary><span>{lang === "en" ? "How long does a divorce take in Texas?" : "¿Cuánto tarda un divorcio en Texas?"}</span></summary>
            <p>{lang === "en" ? "Texas requires a 60-day waiting period from the filing date. Contested cases typically take 6–12 months." : "Texas requiere un período de espera de 60 días desde la fecha de presentación. Los casos contenciosos toman de 6 a 12 meses."}</p>
          </details>
        </div>

        <h2 style={{marginTop: 48}}>Contact Info</h2>
        <div className="gw-card">
          <p><strong>{BUSINESS.name}</strong></p>
          <p>{BUSINESS.street}, {BUSINESS.city}, {BUSINESS.state} {BUSINESS.postal}</p>
          <p><a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phoneDisplay}</a></p>
        </div>
      </section>
    </>
  );
}
